import React, { useState, useCallback, useEffect } from 'react';
import { ISelectOption, ISelectFieldValue } from 'react-ocean-forms-bootstrap';
import { ISelectOptions } from "react-ocean-forms-bootstrap";
import { useQuery } from '@apollo/client';
import { ViewConfigField } from '../../viewConfig.types';
import { AsyncPageableSelect } from '../AsyncPageableSelect';
import { ActionMeta } from 'react-select/lib/types';
import { KomponenteStatus } from '../../../generated/types';

interface BaseComponentSelectProps {
  name: string;
  searchValue?: string | null;
  query: any;
  path: string;
  getLabel?(e: any): string;
  handleChange?(value: ISelectFieldValue, action?: ActionMeta): void;
  fieldConfig?: ViewConfigField;
  inputSize?: string;
  labelSize?: string;
  multi?: boolean;
  status?: KomponenteStatus[];
}

type ComponentSelectProps = BaseComponentSelectProps;

/**
 * @author Jungwirth
 * 
 */
export const ComponentAsyncSelect: React.FC<ComponentSelectProps> = (props: Readonly<ComponentSelectProps>): JSX.Element => {
  const { name, searchValue, query, status = [], getLabel, path, fieldConfig, handleChange, multi, inputSize = '9', labelSize = '3' } = props;
  const [value, setValue] = useState<ISelectOption|undefined|null>(undefined);

  const { fetchMore, refetch } = useQuery(
    query,
    {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      skip: true,
    }
  );

  /** search for controlled value in list */
  useEffect(() => {
    const loadingValue = async () => {
      if(searchValue) {
        // console.log('SEARCH VALUE', searchValue);
        const result = await refetch({ number: 1, size: 1, name: searchValue });
        // console.log('value available in list: ', propValue, result);
        if(result.data && result.data[path]['data'].length >= 1) {
          const resultValue = result.data[path]['data'][0];
          setValue({label: getLabel ? getLabel(resultValue) : resultValue.name, value: JSON.stringify(resultValue)});
        }

      } else {
        // uncontrolled 
        setValue(undefined);
      }
    } 
    loadingValue();
  },[searchValue, refetch, path, getLabel]);

  /**
   * loading options on open dropdown and scroll to bottom of dropdown
   */
  const loadOptionsCallback = useCallback(
    async (search: string, prevOptions: ISelectOption[], additional: any) => {
      // console.log('load further components',search, prevOptions, additional);
      const result = await fetchMore<any,any>({
        variables: {number: (additional.number), size: additional.size, name: search, status: status},
        updateQuery: (prev, {fetchMoreResult}) => {
          if (!fetchMoreResult) return prev;
        }
      });
      const newOptions: ISelectOptions = [];
      result.data[path]['data'].forEach((e: any) => {
        // @ts-ignore
        newOptions.push({label: getLabel ? getLabel(e) : e.name, value: JSON.stringify(e), isFixed: false });
      });
      const total = result.data[path]['page'].totalElements;
      const allOptions = [...prevOptions, ...newOptions];
      
      return {
        options: [...newOptions],
        hasMore: allOptions.length < total,
        additional: {
          number: additional.number +1,
          size: additional.size,
        }
      }
    },[fetchMore, path, getLabel, status]
  );

  // console.log('Component', name, searchValue, value);

  return (
    <React.Fragment>
      <AsyncPageableSelect
        name={name}
        label={name}
        loadOptions={loadOptionsCallback}
        handleChange={handleChange}
        additional={{number:1, size: 50}}
        noOptionsMessage="dropdown_component_no_result"
        loadingMessage="dropdown_component_loading"
        clearable
        inputSize={inputSize}
        labelSize={labelSize}
        valueControlled={value}
        multi={multi}
        {...fieldConfig}
      />
    </React.Fragment>
  );
};
